import { Link } from "react-router-dom";
import { venueUrl } from "../utils/venueUrl";
import { useTranslation } from "react-i18next";
import { MapPin, Star, Utensils, Footprints } from "lucide-react";
import { useUserLocation, distanceKm, formatDistance } from "../utils/useUserLocation";

export default function VenueCard({ venue }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { coords } = useUserLocation();
  const L = (field) => lang === "el" ? (venue[`${field}_el`] || venue[field] || "") : (venue[field] || "");

  const km = coords && venue?.latitude && venue?.longitude
    ? distanceKm(coords.lat, coords.lng, venue.latitude, venue.longitude)
    : null;
  const unit = t("common:km", { defaultValue: "km" });

  return (
    <Link
      to={venueUrl(venue)}
      className="group block"
      data-testid={`venue-card-${venue.venue_id}`}
    >
      <div className="relative overflow-hidden rounded-xl aspect-[4/3] bg-muted mb-4">
        {venue.hero_image ? (
          <img
            src={venue.hero_image}
            alt={L("name")}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
            <Utensils className="h-12 w-12 text-muted-foreground" />
          </div>
        )}
        {(venue.rating_average || venue.average_review_score) > 0 && (
          <div className="absolute top-4 right-4 flex items-center gap-1 bg-background/90 backdrop-blur-sm px-2 py-1 rounded-full" data-testid={`venue-card-rating-${venue.venue_id}`}>
            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
            <span className="text-sm font-medium tabular-nums">
              {Number(venue.rating_average ?? venue.average_review_score).toFixed(1)}
            </span>
            {(venue.rating_count || venue.review_count) > 0 && (
              <span className="text-xs text-muted-foreground tabular-nums">
                · {venue.rating_count ?? venue.review_count}
              </span>
            )}
          </div>
        )}
        {venue.is_featured && (
          <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-medium">
            {t("common:featured", { defaultValue: "Featured" })}
          </div>
        )}
        {km != null && (
          <div
            className="absolute bottom-3 left-3 flex items-center gap-1 bg-background/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium text-foreground"
            data-testid={`venue-card-distance-${venue.venue_id}`}
          >
            <Footprints className="h-3 w-3 text-primary" />
            {formatDistance(km, unit)}
          </div>
        )}
      </div>
      <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">{L("name")}</h3>
      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
        <span>{L("venue_type_name")}</span>
        {venue.cuisine_name && (
          <>
            <span>&bull;</span>
            <span>{L("cuisine_name")}</span>
          </>
        )}
        {venue.city_name && (
          <>
            <span>&bull;</span>
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {/* If the venue has an Area, surface it next to the city for
                  faster local context (e.g., "Strovolos · Nicosia"). */}
              {venue.area_name ? (
                <>
                  {L("area_name")}
                  <span className="text-muted-foreground/60">·</span>
                  <span>{L("city_name")}</span>
                </>
              ) : (
                L("city_name")
              )}
            </span>
          </>
        )}
      </div>
      {(venue.description_short || venue.description_short_el) && (
        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{L("description_short")}</p>
      )}
    </Link>
  );
}
